import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom'; 
import API from './services/api';

// 🌟 BULLETPROOF IMAGE COMPONENT
const ImageWithFallback = ({ src, alt }) => {
    const [hasError, setHasError] = useState(false);

    if (hasError || !src) {
        return (
            <div style={{width: '100%', height: '100%', display:'flex', alignItems:'center', justifyContent:'center', background: 'linear-gradient(135deg, #001d3d, #003566)'}}>
                <i className="fas fa-image" style={{fontSize: '4rem', color: 'rgba(255, 195, 0, 0.2)'}}></i>
            </div>
        );
    }

    return (
        <img 
            src={src} 
            alt={alt} 
            onError={() => setHasError(true)} 
            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 2s cubic-bezier(0.2, 0.8, 0.2, 1)' }}
            className="aesthetic-zoom"
        />
    );
};

const Home = () => {
    const [slides, setSlides] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [events, setEvents] = useState([]); 
    const [upcomingEvents, setUpcomingEvents] = useState([]); 
    
    const [expandedEvent, setExpandedEvent] = useState(null);
    const [user, setUser] = useState(null);
    const [suggestion, setSuggestion] = useState('');
    const [status, setStatus] = useState('');

    // 🌟 NEW: State for Mobile App Download Popup
    // const [showAppPopup, setShowAppPopup] = useState(false);

    const navigate = useNavigate();
    const location = useLocation(); 
    const BACKEND_URL = "https://sherianalumni.com/api/";

    // 🌟 NEW: Mobile Detection & Popup Logic
    /*
    useEffect(() => {
        // Check if device is mobile
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        // Check if user has already seen the popup in this session
        const hasSeenPopup = sessionStorage.getItem('sherianAppPopupSeen');

        if (isMobile && !hasSeenPopup) {
            // Slight delay (2.5s) so it feels natural and doesn't block immediate loading
            const timer = setTimeout(() => {
                setShowAppPopup(true);
                sessionStorage.setItem('sherianAppPopupSeen', 'true');
            }, 2500);
            return () => clearTimeout(timer);
        }
    }, []);
    */

    // 🌟 HASH SCROLL LOGIC
    useEffect(() => {
        if (location.hash) {
            const id = location.hash.replace('#', '');
            const element = document.getElementById(id);
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 500);
            }
        }
    }, [location]);

    // 🌟 AESTHETIC SCROLL REVEAL LOGIC
    useEffect(() => {
        const observerOptions = { threshold: 0.15, rootMargin: "0px 0px -50px 0px" };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-revealed');
                    observer.unobserve(entry.target); 
                }
            });
        }, observerOptions);

        const revealElements = document.querySelectorAll('.reveal-elem');
        revealElements.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [events, upcomingEvents]); 

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) setUser(JSON.parse(storedUser));

        API.get('/get_hero.php')
            .then(res => {
                if (Array.isArray(res.data)) {
                    setSlides(res.data);
                } else {
                    throw new Error("Data is not an array");
                }
            })
            .catch(err => {
                setSlides([{
                    image_url: 'https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=1500',
                    title: 'Once a Cadet, Always a Sherian',
                    subtitle: 'The official bridge between the past and future of KSKCC.'
                }]);
            });

        API.get('/get_events.php').then(res => {
            if (Array.isArray(res.data)) setEvents(res.data);
        }).catch(err => console.error("Events error", err));

        API.get('/get_upcoming_events.php').then(res => {
            if (Array.isArray(res.data)) setUpcomingEvents(res.data);
        }).catch(err => console.error("Upcoming Events error", err));
    }, []);

    useEffect(() => {
        if (slides.length > 1) {
            const timer = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
            }, 7000);
            return () => clearInterval(timer);
        }
    }, [slides]);

    const handleSuggestionSubmit = async (e) => {
        e.preventDefault();
        try {
            const res = await API.post('/submit_suggestion.php', { user_id: user.id, message: suggestion });
            setStatus(res.data.message);
            setSuggestion('');
            setTimeout(() => setStatus(''), 5000);
        } catch (err) { alert("Error sending suggestion."); }
    };

    const currentSlide = slides[currentIndex] || slides[0]; 
    const getHeroImage = () => {
        if (!currentSlide?.image_url) return "";
        return currentSlide.image_url.startsWith('http') ? currentSlide.image_url : `${BACKEND_URL}${currentSlide.image_url}`;
    };

    const getEventImage = (imagePath) => {
        if (!imagePath) return "";
        if (imagePath.startsWith('http')) return imagePath;
        let cleanPath = imagePath.replace(/^(\.\.\/api\/|\/api\/|\/)/, ''); 
        cleanPath = cleanPath.startsWith('api/') ? cleanPath.substring(4) : cleanPath;
        return encodeURI(`${BACKEND_URL}${cleanPath}`); 
    };

    const toggleExpand = (id) => setExpandedEvent(expandedEvent === id ? null : id);

    return (
        <div className="home-wrapper">
            <style>{`
    .home-wrapper { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background: #f8fafc; padding-top: 80px; overflow-x: hidden; width: 100vw; max-width: 100%; scroll-behavior: smooth; }
    .home-wrapper *, .home-wrapper *::before, .home-wrapper *::after { box-sizing: border-box; }
    
    #grand-gatherings { scroll-margin-top: 100px; }

    /* 🌟 RESTORED ALL ANIMATION CLASSES */
    .reveal-elem { opacity: 0; transition: all 1.2s cubic-bezier(0.22, 1, 0.36, 1); }
    .reveal-elem.is-revealed { opacity: 1; }
    .anim-up { transform: translateY(50px); }
    .anim-up.is-revealed { transform: translateY(0); }
    .anim-left { transform: translateX(-50px); }
    .anim-left.is-revealed { transform: translateX(0); }
    .anim-scale { transform: scale(0.9); }
    .anim-scale.is-revealed { transform: scale(1); }
    .anim-blur { filter: blur(10px); transform: translateY(30px); }
    .anim-blur.is-revealed { filter: blur(0); transform: translateY(0); }

    /* --- 🌟 PREMIUM HERO SECTION --- */
    .hero-banner { 
        min-height: 100vh; width: 100vw; position: relative; 
        left: 50%; right: 50%; margin-left: -50vw; margin-right: -50vw;
        display: flex; align-items: center; justify-content: start; text-align: left; overflow: hidden;
        padding-top: 80px;
        padding-bottom: 120px;
    }

    .hero-image-frame { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 1; overflow: hidden; }

    .hero-bg-image { 
        width: 100%; height: 100%; background-size: cover; background-position: center; 
        animation: subtleZoom 30s infinite alternate linear; 
    }
    
    .hero-gradient-overlay { 
        position: absolute; top: 0; left: 0; right: 0; bottom: 0; 
        background: linear-gradient(90deg, #092e18 0%, rgba(9, 46, 24, 0.9) 35%, rgba(9, 46, 24, 0.4) 70%, rgba(9, 46, 24, 0) 100%);
        z-index: 2; 
    }

    .hero-watermark { position: absolute; right: -5%; top: 20%; width: 50vw; max-width: 600px; opacity: 0.15; z-index: 3; pointer-events: none; }
    .hero-watermark img { width: 100%; height: auto; }
    
    .hero-content-container { padding-left: 8%; z-index: 10; max-width: 800px; position: relative; top: -60px; }
    
    .hero-overline { color: #ffc300; font-size: 0.9rem; letter-spacing: 2px; text-transform: uppercase; font-weight: 700; margin-bottom: 15px; }

    .hero-title { font-size: clamp(3rem, 6vw, 5rem); font-weight: 900; color: white; line-height: 1.05; margin-bottom: 25px; font-family: 'Arial Black', Impact, sans-serif; text-transform: uppercase; }
    .hero-title span { color: #ffc300; }

    .hero-subtitle { font-size: clamp(1.1rem, 1.5vw, 1.25rem); color: #f1f5f9; margin-bottom: 40px; font-weight: 400; max-width: 600px; line-height: 1.6; }
    
    .hero-buttons { display: flex; gap: 20px; align-items: center; flex-wrap: wrap; }

    .hero-btn-solid { display: inline-flex; align-items: center; background: #ffc300; color: #092e18; border: none; padding: 14px 30px; font-size: 0.95rem; font-weight: 800; border-radius: 8px; text-transform: uppercase; cursor: pointer; transition: 0.3s; }
    .hero-btn-solid:hover { background: #e6b000; transform: translateY(-2px); }

    .hero-btn-outline { display: inline-flex; align-items: center; background: transparent; color: #ffffff; border: 1.5px solid #ffffff; padding: 14px 30px; font-size: 0.95rem; font-weight: 700; border-radius: 8px; text-transform: uppercase; cursor: pointer; transition: 0.3s; }
    .hero-btn-outline:hover { background: rgba(255,255,255,0.1); transform: translateY(-2px); }

    .hero-stats-wrapper {
        position: absolute;
        bottom: 25px;
        right: 0;
        width: 100%;
        display: flex;
        justify-content: flex-end;
        z-index: 10;
        pointer-events: none; 
    }

    .hero-stats-bar {
        pointer-events: auto; 
        background: rgba(9, 46, 24, 0.9); 
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-right: none; 
        border-radius: 40px 0 0 40px; 
        padding: 20px 40px 20px 30px; 
        display: flex;
        align-items: center;
        gap: 25px; 
        box-shadow: -15px 15px 40px rgba(0,0,0,0.25);
    }

    .stat-item { display: flex; align-items: center; gap: 15px; }
    .stat-icon { width: 50px; height: 50px; border-radius: 50%; border: 1.5px solid #ffc300; display: flex; align-items: center; justify-content: center; color: #ffc300; font-size: 1.2rem; }
    
    .stat-info h4 { color: #ffffff; font-size: 1.5rem; font-weight: 800; margin: 0 0 4px 0; line-height: 1;}
    .stat-info p { color: #d1d5db; font-size: 0.85rem; margin: 0; font-weight: 500;}
    
    .stat-divider { width: 1px; height: 40px; background: rgba(255,255,255,0.15); }

    .hero-curve-divider { position: absolute; bottom: 0; left: 0; width: 100%; overflow: hidden; line-height: 0; z-index: 4; transform: translateY(1px); }
    .hero-curve-divider svg { display: block; width: calc(100% + 1.3px); height: 80px; }
    .hero-curve-divider .shape-fill { fill: #f8fafc; }
    .hero-curve-divider .shape-stroke { stroke: #ffc300; stroke-width: 10px; fill: none; }

    /* --- TITLES --- */
    .section-title { text-align: center; font-weight: 900; color: #134628; text-transform: uppercase; margin-bottom: 50px; font-size: 2.2rem; position: relative; }
    .section-title::after { content: ''; position: absolute; bottom: -15px; left: 50%; transform: translateX(-50%); width: 80px; height: 6px; background: #ffc300; border-radius: 10px; }

    /* --- CARDS --- */
    .upcoming-events-section { padding: 80px 0; background: #f8fafc; }
    .upcoming-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; max-width: 1200px; margin: 0 auto; padding: 0 20px; }
    .nl-card { background: #134628; border-radius: 25px; overflow: hidden; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1); transition: 0.5s; cursor: pointer; position: relative; display: flex; flex-direction: column; }
    .nl-card:hover { transform: translateY(-10px); box-shadow: 0 25px 50px rgba(19, 70, 40, 0.3); }
    .nl-img-container { height: 200px; position: relative; overflow: hidden; }
    .nl-date-badge { position: absolute; top: 15px; right: 15px; z-index: 10; background: #ffc300; color: #134628; padding: 10px 14px; border-radius: 15px; text-align: center; font-weight: 900; }
    .nl-content { padding: 25px; display: flex; flex-direction: column; flex-grow: 1; }
    .nl-title { font-weight: 800; font-size: 1.25rem; margin-bottom: 12px; color: #ffffff; line-height: 1.3;}
    .nl-desc { font-size: 0.95rem; line-height: 1.5; color: #cbd5e1; flex-grow: 1; margin-bottom: 20px; }
    .nl-action { display: inline-flex; align-items: center; justify-content: center; background: rgba(255, 195, 0, 0.1); color: #ffc300; padding: 8px 22px; border-radius: 50px; font-weight: 800; text-transform: uppercase; font-size: 0.8rem; transition: 0.3s; gap: 8px; width: fit-content; }
    .nl-card:hover .nl-action { background: #ffc300; color: #134628; }

    /* --- FEED --- */
    .updates-section { padding: 80px 0; background: #0c2d19; } 
    .updates-section .section-title { color: white; }
    .event-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; max-width: 1200px; margin: 0 auto; padding: 0 20px; }
    .event-card { background: #134628; border-radius: 30px; overflow: hidden; position: relative; box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2); transition: 0.5s; border: 1px solid rgba(255, 195, 0, 0.1); display: flex; flex-direction: column; }
    .event-card:hover { background: #ffc300; transform: translateY(-10px); }
    .event-card-content { padding: 30px; display: flex; flex-direction: column; flex-grow: 1; }
    .event-card h3 { color: #ffffff; font-weight: 800; font-size: 1.25rem; margin-bottom: 10px; transition: 0.3s; }
    .event-card p { color: #ffffff; font-size: 0.95rem; line-height: 1.5; transition: 0.3s; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; }
    .event-card p.expanded-text { -webkit-line-clamp: unset; display: block; }
    .event-card:hover h3, .event-card:hover p, .event-card:hover .read-more-btn { color: #134628; }
    
    .feed-image-box { width: 100%; height: 250px; overflow: hidden; background: #092e18; }
    .card-icon-box { width: 50px; height: 50px; background: rgba(255, 195, 0, 0.1); border-radius: 18px; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; color: #ffc300; font-size: 1.4rem; transition: 0.3s; }
    .event-card:hover .card-icon-box { background: #134628; color: #ffc300; }
    .read-more-btn { background: none; border: none; color: #ffc300; font-weight: 800; font-size: 0.8rem; text-transform: uppercase; margin-top: 10px; cursor: pointer; display: inline-flex; align-items: center; }

    /* --- MISSION SECTION --- */
    .about-summary { background: #134628; padding: 80px 20px; text-align: center; color: white; border-top: 6px solid #ffc300; }
    .about-summary h2 { color: #ffc300; font-weight: 900; text-transform: uppercase; margin-bottom: 25px; font-size: 2.2rem; letter-spacing: 2px; }
    .about-summary p { max-width: 850px; margin: 0 auto; color: #f1f5f9; font-size: 1.2rem; line-height: 1.8; font-weight: 300; }

    /* --- SUGGESTIONS --- */
    .suggestion-section { background: linear-gradient(rgba(19, 70, 40, 0.93), rgba(19, 70, 40, 0.93)), url('https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=2070'); background-size: cover; background-attachment: fixed; padding: 80px 20px; text-align: center; }
    .suggestion-container { max-width: 750px; margin: 0 auto; background: rgba(255, 255, 255, 0.08); padding: 40px 30px; border-radius: 35px; backdrop-filter: blur(20px); }
    .suggestion-input { background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1); color: white; font-size: 1.1rem; border-radius: 20px; padding: 20px; width: 100%; outline: none; margin-bottom: 25px; box-sizing: border-box; }
    .btn-submit-sugg { background: linear-gradient(45deg, #ffc300, #ffb703); color: #134628; padding: 15px 50px; font-size: 1rem; border-radius: 50px; font-weight: 900; border: none; cursor: pointer; text-transform: uppercase; }

    @keyframes shineGold { to { background-position: 200% center; } }
    @keyframes subtleZoom { 0% { transform: scale(1); } 100% { transform: scale(1.15); } }
    @keyframes aestheticFadeUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }

    /* 🌟 NEW: APP DOWNLOAD POPUP CSS */
    .app-popup-overlay {
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        background: rgba(9, 46, 24, 0.85);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        z-index: 9999; display: flex; align-items: center; justify-content: center;
        animation: fadeInOverlay 0.4s ease-out forwards;
        padding: 20px;
    }
    .app-popup-card {
        background: #134628; border: 2px solid rgba(255, 195, 0, 0.3);
        border-radius: 25px; width: 100%; max-width: 350px;
        padding: 30px 20px; text-align: center; position: relative;
        box-shadow: 0 25px 50px rgba(0,0,0,0.5);
        animation: scaleUpModal 0.5s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    }
    .app-popup-close {
        position: absolute; top: 15px; right: 15px;
        background: rgba(255,255,255,0.1); border: none; color: white;
        width: 30px; height: 30px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer; transition: 0.3s;
    }
    .app-popup-close:hover { background: #ffc300; color: #134628; }
    .app-popup-icon {
        width: 70px; height: 70px; background: rgba(255, 195, 0, 0.1);
        border-radius: 20px; color: #ffc300; font-size: 2rem;
        display: flex; align-items: center; justify-content: center;
        margin: 0 auto 20px auto;
    }
    .app-popup-title { color: white; font-weight: 900; font-size: 1.4rem; margin-bottom: 10px; }
    .app-popup-text { color: #cbd5e1; font-size: 0.95rem; line-height: 1.5; margin-bottom: 25px; }
    .app-popup-btn {
        display: flex; align-items: center; justify-content: center; gap: 10px;
        background: #ffc300; color: #134628; text-decoration: none;
        padding: 14px 20px; border-radius: 50px; font-weight: 900;
        text-transform: uppercase; width: 100%; font-size: 0.9rem; transition: 0.3s;
    }
    .app-popup-btn:hover { background: #e6b000; transform: translateY(-3px); }
    
    @keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }
    @keyframes scaleUpModal { from { opacity: 0; transform: scale(0.8) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }

    /* --- 📱 PROFESSIONAL MOBILE REFINEMENT --- */
    @media (max-width: 992px) {
        .upcoming-grid, .event-grid { grid-template-columns: repeat(2, 1fr); }
        
        .hero-stats-wrapper {
            position: relative;
            bottom: auto;
            justify-content: center;
            margin-top: 40px;
        }

        .hero-stats-bar { 
            border-radius: 16px; 
            border-right: 1px solid rgba(255,255,255,0.08); 
            flex-wrap: wrap; 
            justify-content: center; 
            width: 90%; 
            padding: 20px; 
        }
        .stat-divider { display: none; }
    }

    /* 🌟 MOBILE STYLING: Sleek, refined, and perfectly proportional widget card */
    @media (max-width: 768px) { 
        .upcoming-grid, .event-grid { grid-template-columns: 1fr; }

        .hero-banner { 
            width: 100%; 
            margin-left: 0; margin-right: 0; 
            left: 0; right: 0; 
            justify-content: center; 
            align-items: center; 
            text-align: center; 
            padding-top: 40px;
            padding-bottom: 240px; /* Reduced to match the smaller stats card */
            min-height: 100vh;
        }
        
        .hero-gradient-overlay { background: rgba(9, 46, 24, 0.8); }
        .hero-watermark { display: none; }

        .hero-content-container { 
            padding: 20px; left: 0; align-items: center; 
            display: flex; flex-direction: column; top: 0; width: 100%; margin: 0 auto; 
        }

        .hero-title { font-size: clamp(2.5rem, 10vw, 3.5rem); margin-bottom: 15px; text-align: center; }
        .hero-subtitle { font-size: 1rem; margin-bottom: 30px; padding: 0 10px; text-align: center; }
        
        .hero-buttons { justify-content: center; width: 100%; display: flex; flex-direction: column; align-items: center; }
        .hero-btn-solid, .hero-btn-outline { width: 100%; max-width: 280px; justify-content: center; }

        .hero-stats-wrapper {
            position: absolute;
            bottom: 30px; 
            width: 100%;
            justify-content: center;
        }

        .hero-stats-bar { 
            border-radius: 20px; 
            padding: 20px 10px; /* Tighter internal padding */
            display: grid;
            grid-template-columns: 1fr 1fr; 
            gap: 20px 5px; /* Tighter grid gaps to save space */
            width: 95%; /* Make full use of small 320px screens */
            max-width: 380px;
            border: 1px solid rgba(255, 195, 0, 0.15);
            box-shadow: 0 10px 30px rgba(0,0,0,0.5); /* Premium shadow depth */
            background: rgba(9, 46, 24, 0.85); /* Slightly transparent glass look */
            border-right: 1px solid rgba(255, 195, 0, 0.15);
        }
        
        .stat-item { 
            width: 100%; 
            justify-content: flex-start; /* Keeps text aligned to the top row consistently */
            align-items: center;
            text-align: center; 
            flex-direction: column; 
            gap: 6px; 
        }
        
        .stat-icon { 
            width: 38px; height: 38px; /* Noticeably smaller */
            font-size: 0.95rem; 
            margin: 0 auto; 
            border-width: 1.5px;
            background: rgba(255, 195, 0, 0.05); /* Subtle inner fill */
        }
        
        .stat-info h4 { 
            font-size: 1.05rem; /* Much crisper number text */
            margin: 0; 
            text-align: center; 
            font-weight: 900;
        }
        
        .stat-info p { 
            font-size: 0.7rem; /* Subtitle won't ugly-wrap anymore */
            text-align: center; 
            line-height: 1.25;
            color: #cbd5e1; /* Subtle slate color for modern contrast */
            padding: 0 4px;
        }

        .stat-divider { display: none; }
        .hero-curve-divider svg { height: 40px; }
    }
`}</style>

            {/* 🌟 NEW: APP DOWNLOAD POPUP UI */}
            {/*
            {showAppPopup && (
                <div className="app-popup-overlay" onClick={() => setShowAppPopup(false)}>
                    <div className="app-popup-card" onClick={(e) => e.stopPropagation()}>
                        <button className="app-popup-close" onClick={() => setShowAppPopup(false)}>
                            <i className="fas fa-times"></i>
                        </button>
                        <div className="app-popup-icon">
                            <i className="fab fa-android"></i>
                        </div>
                        <h3 className="app-popup-title">Get the Sherian App</h3>
                        <p className="app-popup-text">
                            Experience the full alumni network, stay updated with events, and connect instantly on your phone.
                        </p>
                        <a href="https://drive.google.com/uc?export=download&id=1gezqabM05f4fjUbS69ZjySg4qb9732_W" className="app-popup-btn">
                         <i className="fas fa-download"></i> Download APK
                        </a>
                    </div>
                </div>
            )}
            */}

            {/* 1. PREMIUM REDESIGNED HERO SLIDER */}
            <section className="hero-banner">
                <div className="hero-image-frame">
                    <div className="hero-bg-image" style={{ backgroundImage: `url("${getHeroImage()}")` }}></div>
                    <div className="hero-gradient-overlay"></div>
                </div>
                
                <div className="hero-watermark">
                    <img src="../logo.jpeg" alt="Sherian Watermark" style={{ filter: 'grayscale(100%) brightness(200%) opacity(0.3)'}} />
                </div>
                
                <div className="hero-content-container">
                    <h4 className="hero-overline reveal-elem anim-up">Karnal Sher Khan Cadet College Swabi</h4>
                    
                    <h1 className="hero-title reveal-elem anim-up" style={{transitionDelay: '0.1s'}}>
                        {currentSlide?.title ? (
                            <>
                                {currentSlide.title.split(' ').slice(0, 3).join(' ')} <br/><span>{currentSlide.title.split(' ').slice(3).join(' ')}</span>
                            </>
                        ) : (
                            <>ALWAYS BE A <br/><span>TRUE SHERIAN</span></>
                        )}
                    </h1>
                    
                    <p className="hero-subtitle reveal-elem anim-up" style={{transitionDelay: '0.2s'}}>
                        {currentSlide?.subtitle || "Connecting generations of Sherians worldwide. Together, we celebrate our legacy and build a stronger future."}
                    </p>
                    
                    <div className="hero-buttons reveal-elem anim-up" style={{transitionDelay: '0.3s'}}>
                        {!user ? (
                            <button className="hero-btn-solid" onClick={() => navigate('/register')}>
                                Explore Our Legacy <i className="fas fa-arrow-right ms-2"></i>
                            </button>
                        ) : (
                            <button className="hero-btn-solid" onClick={() => navigate('/dashboard')}>
                                My Dashboard <i className="fas fa-user-shield ms-2"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* 🌟 STATS WRAPPER */}
                <div className="hero-stats-wrapper reveal-elem anim-up" style={{transitionDelay: '0.4s'}}>
                    <div className="hero-stats-bar">
                        <div className="stat-item">
                            <div className="stat-icon"><i className="fas fa-users"></i></div>
                            <div className="stat-info">
                                <h4>12+ Years</h4>
                                <p>Building Leaders Since 2011</p>
                            </div>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <div className="stat-icon"><i className="fas fa-globe"></i></div>
                            <div className="stat-info">
                                <h4>15+ Batches</h4>
                                <p>United by the Sherian Spirit</p>
                            </div>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <div className="stat-icon"><i className="fas fa-graduation-cap"></i></div>
                            <div className="stat-info">
                                <h4>1,800+ Sherians</h4>
                                <p>One Alumni Network</p>
                            </div>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <div className="stat-icon"><i className="fas fa-handshake"></i></div>
                            <div className="stat-info">
                                <h4>One</h4>
                                <p>Sherian Family</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="hero-curve-divider">
                    <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
                        <path d="M0,0 C300,100 900,100 1200,0 L1200,120 L0,120 Z" className="shape-fill"></path>
                        <path d="M0,0 C300,100 900,100 1200,0" className="shape-stroke"></path>
                    </svg>
                </div>
            </section>

            {/* 2. GRAND GATHERINGS */}
            <section id="grand-gatherings" className="upcoming-events-section">
                <div className="container">
                    <h2 className="section-title reveal-elem anim-up">Grand Gatherings</h2>
                    <div className="upcoming-grid">
                        {Array.isArray(upcomingEvents) && upcomingEvents.map((event, index) => {
                            const eventDate = new Date(event.event_date);
                            return (
                                <div key={`upcoming-${event.id}`} className="nl-card reveal-elem anim-up" onClick={() => navigate(`/event/${event.id}`)} style={{ transitionDelay: `${index * 0.15}s` }}>
                                    <div className="nl-img-container">
                                        <div className="nl-date-badge">
                                            <span style={{fontSize: '1.6rem', display: 'block'}}>{eventDate.getDate()}</span>
                                            <span style={{fontSize: '0.7rem', textTransform: 'uppercase'}}>{eventDate.toLocaleString('default', { month: 'short' })}</span>
                                        </div>
                                        <ImageWithFallback src={getEventImage(event.event_image)} alt={event.title} />
                                    </div>
                                    <div className="nl-content">
                                        <h3 className="nl-title">{event.title}</h3>
                                        <p className="nl-desc">{event.description.substring(0, 100)}...</p>
                                        <div className="nl-action">Explore Event <i className="fas fa-arrow-right"></i></div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* 3. SHERIAN FEED */}
            <section className="updates-section">
                <div className="container">
                    <h2 className="section-title reveal-elem anim-left">Sherian Feed</h2>
                    <div className="event-grid">
                        {Array.isArray(events) && events.map((event, index) => {
                            const isExpanded = expandedEvent === event.id;
                            const imagePath = event.image || event.image_url || event.event_image || event.picture;

                            return (
                                <div key={`update-${event.id}`} className="event-card reveal-elem anim-left" style={{ transitionDelay: `${index * 0.15}s` }}>
                                    {imagePath && (
                                        <div className="feed-image-box">
                                            <ImageWithFallback src={getEventImage(imagePath)} alt={event.title} />
                                        </div>
                                    )}

                                    <div className="event-card-content">
                                        {!imagePath && (
                                            <div className="card-icon-box"><i className="fas fa-bolt"></i></div>
                                        )}

                                        <h3>{event.title}</h3>
                                        <p className={isExpanded ? "expanded-text" : ""}>{event.description}</p>
                                        
                                        {event.description.length > 100 && (
                                            <button onClick={() => toggleExpand(event.id)} className="read-more-btn">
                                                {isExpanded ? 'Show Less' : 'Read More'} <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} ms-2`}></i>
                                            </button>
                                        )}
                                        
                                        <div style={{marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid rgba(255,195,0,0.3)'}}>
                                            <small style={{color: '#ffc300', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px'}}>
                                                <i className="far fa-clock me-2"></i>
                                                {new Date(event.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* 4. THE MISSION */}
            <section className="about-summary">
                <div className="container">
                    <h2 className="mb-4 reveal-elem anim-scale">The Sherian Mission</h2>
                    <p className="reveal-elem anim-scale" style={{transitionDelay: '0.15s'}}>
                        To unite Sherians worldwide in a lifelong bond of brotherhood, empowering alumni to lead, serve, mentor, and give back through initiatives that strengthen Karnal Sher Khan Cadet College, support fellow Sherians, and create lasting impact for future generations.
                    </p>
                </div>
            </section>

            {/* 5. SUGGESTIONS */}
            {user && (
                <section className="suggestion-section">
                    <div className="container">
                        <div className="suggestion-container reveal-elem anim-blur">
                            <h2 style={{color: '#ffc300', fontWeight: 900, textTransform: 'uppercase', marginBottom: '20px', fontSize: '1.8rem'}}>Sherian Voice</h2>
                            <p style={{marginBottom: '40px', color: '#f1f5f9', fontSize: '1.1rem', opacity: 0.9, fontWeight: 300}}>
                                Your leadership matters. Share your feedback directly with the association board.
                            </p>
                            <form onSubmit={handleSuggestionSubmit}>
                                <textarea className="suggestion-input" rows="5" placeholder="Deliver your message, Sherian..." value={suggestion} onChange={(e) => setSuggestion(e.target.value)} required></textarea>
                                <button type="submit" className="btn-submit-sugg">Send Message <i className="fas fa-paper-plane ms-2"></i></button>
                            </form>
                            {status && <div className="success-msg" style={{marginTop: '35px', color: '#00f5d4', fontSize: '1.1rem', fontWeight: 800}}><i className="fas fa-check-circle me-2"></i>{status}</div>}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default Home;